import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import {
  createBillingPlan,
  createBillingPlanVersion,
  getBillingAddonCatalogAdmin,
  getBillingPlanCatalog,
  seedOfficialBillingAddonPackages,
  seedTelephonySmsAddonPrices,
  updateBillingPlanLimits,
  upsertBillingAddonPriceAdmin,
} from "../../../api/client";

const defaultLimitRows = () => [{ metricKey: "interactions", limitType: "included", limitValue: 10000 }];

const CatalogTabPanel = ({ onError, onSuccess }) => {
  const { t } = useTranslation("app");
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
  }, [onError, onSuccess]);

  const [catalogBusy, setCatalogBusy] = useState(false);
  const [seedingOfficial, setSeedingOfficial] = useState(false);
  const [seedingTelephonySms, setSeedingTelephonySms] = useState(false);
  const [lastSeededSkus, setLastSeededSkus] = useState([]);
  const [lastSeededLabel, setLastSeededLabel] = useState("");
  const [newPlan, setNewPlan] = useState({ sku: "", name: "", audience: "end_customer" });
  const [newVersion, setNewVersion] = useState({ sku: "", version: 1, status: "draft" });
  const [limitsSku, setLimitsSku] = useState("");
  const [limitsVersion, setLimitsVersion] = useState(1);
  const [limitRows, setLimitRows] = useState(defaultLimitRows);
  const [limitsJsonOpen, setLimitsJsonOpen] = useState(false);
  const [limitsJson, setLimitsJson] = useState(
    '[{"metricKey":"interactions","limitType":"included","limitValue":10000}]'
  );
  const [addonItems, setAddonItems] = useState([]);
  const [addonLoading, setAddonLoading] = useState(false);
  const [priceDialog, setPriceDialog] = useState({ open: false, sku: "", row: null });
  const [priceForm, setPriceForm] = useState({
    chargeType: "package",
    billingMode: "all",
    quantity: 1,
    amountCents: 0,
    currencyCode: "BRL",
  });

  const loadCatalog = useCallback(async () => {
    await getBillingPlanCatalog();
  }, []);

  const loadAddons = useCallback(async () => {
    setAddonLoading(true);
    try {
      const res = await getBillingAddonCatalogAdmin();
      setAddonItems(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      onErrorRef.current?.(err?.message || "addons");
    } finally {
      setAddonLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
    loadAddons();
  }, [loadCatalog, loadAddons]);

  const groupedAddons = useMemo(() => {
    const map = new Map();
    for (const row of addonItems) {
      const sku = row.sku || "";
      if (!map.has(sku)) {
        map.set(sku, {
          sku,
          name: row.name,
          addon_type: row.addon_type,
          metric_key: row.metric_key,
          active: row.active,
          prices: [],
        });
      }
      if (row.price_id != null) {
        map.get(sku).prices.push(row);
      }
    }
    return [...map.values()].sort((a, b) => a.sku.localeCompare(b.sku));
  }, [addonItems]);

  const handleCreatePlan = async () => {
    setCatalogBusy(true);
    onError?.("");
    try {
      await createBillingPlan({
        sku: newPlan.sku.trim().toLowerCase(),
        name: newPlan.name.trim(),
        audience: newPlan.audience,
        active: true,
      });
      onSuccess?.(t("billingPage.adminMsgPlanSaved"));
      await loadCatalog();
    } catch (err) {
      onError?.(err?.message || "plan");
    } finally {
      setCatalogBusy(false);
    }
  };

  const handleCreateVersion = async () => {
    setCatalogBusy(true);
    onError?.("");
    try {
      await createBillingPlanVersion(newVersion.sku.trim().toLowerCase(), {
        version: Number(newVersion.version),
        status: newVersion.status,
      });
      onSuccess?.(t("billingPage.adminMsgVersionSaved"));
      await loadCatalog();
    } catch (err) {
      onError?.(err?.message || "version");
    } finally {
      setCatalogBusy(false);
    }
  };

  const syncJsonFromRows = () => {
    const payload = limitRows
      .filter((r) => String(r.metricKey || "").trim())
      .map((r) => ({
        metricKey: String(r.metricKey).trim(),
        limitType: r.limitType || "included",
        limitValue: r.limitValue === "" || r.limitValue == null ? null : Number(r.limitValue),
      }));
    setLimitsJson(JSON.stringify(payload, null, 0));
  };

  const handleSaveLimits = async () => {
    setCatalogBusy(true);
    onError?.("");
    try {
      let parsed;
      if (limitsJsonOpen) {
        parsed = JSON.parse(limitsJson || "[]");
      } else {
        parsed = limitRows
          .filter((r) => String(r.metricKey || "").trim())
          .map((r) => ({
            metricKey: String(r.metricKey).trim(),
            limitType: r.limitType || "included",
            limitValue: r.limitValue === "" || r.limitValue == null ? null : Number(r.limitValue),
          }));
      }
      if (!Array.isArray(parsed)) throw new Error("Limites devem ser um array.");
      await updateBillingPlanLimits(limitsSku.trim().toLowerCase(), Number(limitsVersion), parsed);
      onSuccess?.(t("billingPage.adminMsgLimitsSaved"));
      await loadCatalog();
    } catch (err) {
      onError?.(err?.message || "limits");
    } finally {
      setCatalogBusy(false);
    }
  };

  const openPriceEdit = (sku, priceRow) => {
    setPriceDialog({ open: true, sku, row: priceRow });
    setPriceForm({
      chargeType: priceRow?.charge_type || "package",
      billingMode: priceRow?.billing_mode || "all",
      quantity: Number(priceRow?.quantity || 1),
      amountCents: Number(priceRow?.amount_cents ?? 0),
      currencyCode: priceRow?.currency_code || "BRL",
    });
  };

  const saveAddonPrice = async () => {
    const sku = priceDialog.sku;
    if (!sku) return;
    setCatalogBusy(true);
    onError?.("");
    try {
      await upsertBillingAddonPriceAdmin(sku, {
        chargeType: priceForm.chargeType,
        billingMode: priceForm.billingMode,
        quantity: Number(priceForm.quantity),
        amountCents: Number(priceForm.amountCents),
        currencyCode: priceForm.currencyCode,
      });
      onSuccess?.(t("platformPage.addonPriceSaved"));
      setPriceDialog({ open: false, sku: "", row: null });
      await loadAddons();
    } catch (err) {
      onError?.(err?.message || "addon");
    } finally {
      setCatalogBusy(false);
    }
  };

  const handleSeedOfficial = async () => {
    setSeedingOfficial(true);
    onError?.("");
    try {
      const res = await seedOfficialBillingAddonPackages();
      setLastSeededSkus(Array.isArray(res?.imported) ? res.imported : []);
      setLastSeededLabel(t("platformPage.seedOfficialLastRun", { defaultValue: "Pacotes oficiais" }));
      onSuccess?.(t("platformPage.seedOfficialSuccess", { defaultValue: "OK" }));
      await loadAddons();
    } catch (err) {
      onError?.(err?.response?.data?.message || err?.message || t("platformPage.seedOfficialError"));
    } finally {
      setSeedingOfficial(false);
    }
  };

  const handleSeedTelephony = async () => {
    setSeedingTelephonySms(true);
    onError?.("");
    try {
      const res = await seedTelephonySmsAddonPrices();
      setLastSeededSkus(Array.isArray(res?.imported) ? res.imported : []);
      setLastSeededLabel(t("platformPage.seedTelephonySmsLastRun", { defaultValue: "Telefonia/SMS" }));
      onSuccess?.(t("platformPage.seedTelephonySmsSuccess", { defaultValue: "OK" }));
      await loadAddons();
    } catch (err) {
      onError?.(err?.response?.data?.message || err?.message || t("platformPage.seedTelephonySmsError"));
    } finally {
      setSeedingTelephonySms(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined">
        <CardContent sx={{ pt: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("platformPage.seedOfficialTitle", { defaultValue: "Importação oficial de pacotes" })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t("platformPage.seedOfficialDesc", {
              defaultValue: "Importa/atualiza pacotes oficiais e preços base de telefonia/SMS.",
            })}
          </Typography>
          <Button variant="contained" color="secondary" onClick={handleSeedOfficial} disabled={seedingOfficial} sx={{ mr: 1 }}>
            {seedingOfficial
              ? t("platformPage.seedOfficialLoading", { defaultValue: "Importando..." })
              : t("platformPage.seedOfficialAction", { defaultValue: "Importar pacotes oficiais" })}
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleSeedTelephony} disabled={seedingTelephonySms}>
            {seedingTelephonySms
              ? t("platformPage.seedTelephonySmsLoading", { defaultValue: "Sincronizando..." })
              : t("platformPage.seedTelephonySmsAction", { defaultValue: "Sincronizar Telefonia/SMS" })}
          </Button>
          {lastSeededSkus.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                {lastSeededLabel}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {lastSeededSkus.map((sku) => (
                  <Chip key={sku} size="small" label={sku} variant="outlined" />
                ))}
              </Stack>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ pt: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("billingPage.adminStep1Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t("billingPage.adminStep1Desc")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("billingPage.adminFieldSkuNew")}
                value={newPlan.sku}
                onChange={(e) => setNewPlan((p) => ({ ...p, sku: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("billingPage.adminFieldName")}
                value={newPlan.name}
                onChange={(e) => setNewPlan((p) => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t("billingPage.adminFieldAudience")}
                value={newPlan.audience}
                onChange={(e) => setNewPlan((p) => ({ ...p, audience: e.target.value }))}
              >
                <MenuItem value="end_customer">{t("billingPage.adminAudienceEndCustomer")}</MenuItem>
                <MenuItem value="reseller">{t("billingPage.adminAudienceReseller")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleCreatePlan} disabled={catalogBusy}>
                {t("billingPage.adminSaveProduct")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ pt: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("billingPage.adminStep2Title")}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("billingPage.adminFieldSkuVersion")}
                value={newVersion.sku}
                onChange={(e) => setNewVersion((p) => ({ ...p, sku: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t("billingPage.adminFieldVersionNumber")}
                value={newVersion.version}
                onChange={(e) => setNewVersion((p) => ({ ...p, version: Number(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t("billingPage.adminFieldVersionStatus")}
                value={newVersion.status}
                onChange={(e) => setNewVersion((p) => ({ ...p, status: e.target.value }))}
              >
                <MenuItem value="draft">{t("billingPage.adminStatusDraft")}</MenuItem>
                <MenuItem value="active">{t("billingPage.adminStatusActive")}</MenuItem>
                <MenuItem value="deprecated">{t("billingPage.adminStatusDeprecated")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleCreateVersion} disabled={catalogBusy}>
                {t("billingPage.adminSaveVersion")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ pt: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("billingPage.adminStep3Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("platformPage.limitsTableHint")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("billingPage.adminFieldSkuLimits")}
                value={limitsSku}
                onChange={(e) => setLimitsSku(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label={t("billingPage.adminFieldVersionNumber")}
                value={limitsVersion}
                onChange={(e) => setLimitsVersion(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setLimitRows((r) => [...r, { metricKey: "", limitType: "included", limitValue: "" }])}>
                  {t("platformPage.limitsAddRow")}
                </Button>
                <Button size="small" onClick={syncJsonFromRows}>
                  {t("platformPage.limitsSyncJson")}
                </Button>
                <IconButton size="small" onClick={() => setLimitsJsonOpen((v) => !v)} aria-label="json">
                  {limitsJsonOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  {t("platformPage.limitsAdvancedJson")}
                </Typography>
              </Stack>
              <TableContainer variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("platformPage.limitsColMetric")}</TableCell>
                      <TableCell>{t("platformPage.limitsColType")}</TableCell>
                      <TableCell>{t("platformPage.limitsColValue")}</TableCell>
                      <TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {limitRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.metricKey}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLimitRows((rows) => rows.map((x, i) => (i === idx ? { ...x, metricKey: v } : x)));
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={row.limitType}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLimitRows((rows) => rows.map((x, i) => (i === idx ? { ...x, limitType: v } : x)));
                            }}
                          >
                            <MenuItem value="included">{t("platformPage.limitTypeIncluded")}</MenuItem>
                            <MenuItem value="unlimited">{t("billingPage.limitUnlimited")}</MenuItem>
                            <MenuItem value="disabled">{t("platformPage.limitTypeDisabled")}</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={row.limitValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLimitRows((rows) => rows.map((x, i) => (i === idx ? { ...x, limitValue: v } : x)));
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setLimitRows((rows) => rows.filter((_, i) => i !== idx))}
                            disabled={limitRows.length <= 1}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Collapse in={limitsJsonOpen}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  sx={{ mt: 1 }}
                  label={t("billingPage.adminFieldLimitsJson")}
                  value={limitsJson}
                  onChange={(e) => setLimitsJson(e.target.value)}
                  InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                />
              </Collapse>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSaveLimits} disabled={catalogBusy}>
                {t("billingPage.adminSaveLimits")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ pt: 2, "&:last-child": { pb: 2 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("platformPage.addonCatalogTitle")}
            </Typography>
            <Button size="small" onClick={loadAddons} disabled={addonLoading}>
              {t("billingPage.refresh")}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t("platformPage.addonCatalogHint")}
          </Typography>
          {addonLoading ? (
            <Typography variant="body2">{t("loading")}</Typography>
          ) : (
            <TableContainer variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>{t("platformPage.addonColName")}</TableCell>
                    <TableCell>{t("platformPage.addonColPrices")}</TableCell>
                    <TableCell align="right">{t("platformPage.colActions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedAddons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("platformPage.addonCatalogEmpty")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupedAddons.map((g) => (
                      <TableRow key={g.sku}>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{g.sku}</TableCell>
                        <TableCell>{g.name}</TableCell>
                        <TableCell>
                          {g.prices.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              {t("platformPage.addonNoPrices")}
                            </Typography>
                          ) : (
                            <Stack spacing={0.25}>
                              {g.prices.map((pr, i) => (
                                <Typography key={i} variant="caption" display="block">
                                  {pr.charge_type} / {pr.billing_mode} · qty {pr.quantity} ·{" "}
                                  {(Number(pr.amount_cents || 0) / 100).toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: pr.currency_code || "BRL",
                                  })}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={() => openPriceEdit(g.sku, g.prices[0] || null)}>
                            {t("platformPage.addonEditPrice")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" variant="outlined">
        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
          {t("platformPage.catalogFootnote")}
        </Typography>
      </Alert>

      <Dialog open={priceDialog.open} onClose={() => setPriceDialog({ open: false, sku: "", row: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{t("platformPage.addonDialogTitle", { sku: priceDialog.sku })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label={t("platformPage.addonFieldChargeType")}
              value={priceForm.chargeType}
              onChange={(e) => setPriceForm((p) => ({ ...p, chargeType: e.target.value }))}
            >
              <MenuItem value="package">{t("platformPage.chargePackage")}</MenuItem>
              <MenuItem value="recurring">{t("platformPage.chargeRecurring")}</MenuItem>
              <MenuItem value="usage">{t("platformPage.chargeUsage")}</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              label={t("billingPage.adminFieldBillingMode")}
              value={priceForm.billingMode}
              onChange={(e) => setPriceForm((p) => ({ ...p, billingMode: e.target.value }))}
            >
              <MenuItem value="all">{t("platformPage.billingModeAll")}</MenuItem>
              <MenuItem value="prepaid">{t("billingPage.billingMode.prepaid")}</MenuItem>
              <MenuItem value="postpaid">{t("billingPage.billingMode.postpaid")}</MenuItem>
              <MenuItem value="hybrid">{t("billingPage.billingMode.hybrid")}</MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="number"
              label={t("platformPage.addonFieldQuantity")}
              value={priceForm.quantity}
              onChange={(e) => setPriceForm((p) => ({ ...p, quantity: Number(e.target.value) }))}
            />
            <TextField
              fullWidth
              type="number"
              label={t("platformPage.addonFieldAmountCents")}
              value={priceForm.amountCents}
              onChange={(e) => setPriceForm((p) => ({ ...p, amountCents: Number(e.target.value) }))}
              helperText={t("platformPage.addonCentsHint")}
            />
            <TextField
              fullWidth
              label={t("platformPage.addonFieldCurrency")}
              value={priceForm.currencyCode}
              onChange={(e) => setPriceForm((p) => ({ ...p, currencyCode: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialog({ open: false, sku: "", row: null })}>{t("platformPage.cancel")}</Button>
          <Button variant="contained" onClick={saveAddonPrice} disabled={catalogBusy}>
            {t("platformPage.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CatalogTabPanel;
